import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastData {
  type: ToastType;
  title?: string;
  message: string;
  duration: number;
  enableHtml?: boolean;
  /** Optional call-to-action; clicking it dismisses the toast with action. */
  actionLabel?: string;
}

@Component({
  selector: 'app-toast-snackbar',
  standalone: true,
  imports: [MatIconModule],
  template: `
    <div
      class="toast-card"
      [class]="'toast-' + data.type"
      (mouseenter)="pause()"
      (mouseleave)="resume()"
    >
      <div class="toast-icon">
        <mat-icon>
          @switch (data.type) {
            @case ('success') { check_circle }
            @case ('error') { error }
            @case ('warning') { warning }
            @default { info }
          }
        </mat-icon>
      </div>
      <div class="toast-body">
        @if (data.title) {
          <div class="toast-title">{{ data.title }}</div>
        }
        @if (data.enableHtml) {
          <div class="toast-message" [innerHTML]="data.message"></div>
        } @else {
          <div class="toast-message">{{ data.message }}</div>
        }
        @if (data.actionLabel) {
          <button
            class="toast-action"
            type="button"
            (click)="snackBarRef.dismissWithAction()"
          >
            {{ data.actionLabel }}
          </button>
        }
      </div>
      <button
        class="toast-close"
        type="button"
        aria-label="Dismiss"
        (click)="snackBarRef.dismiss()"
      >
        <mat-icon>close</mat-icon>
      </button>
      <div
        class="toast-progress"
        [class.run]="hasEntered"
        [class.paused]="isPaused"
        [style.animation-duration.ms]="data.duration"
      ></div>
    </div>
  `,
  styles: [
    `
      .toast-card {
        position: relative;
        display: flex;
        align-items: center;
        gap: 14px;
        min-width: 360px;
        max-width: 460px;
        padding: 16px 42px 16px 18px;
        border-radius: 14px;
        border: 1.5px solid var(--toast-border, #c7d2fe);
        background: linear-gradient(90deg, var(--toast-tint, #eef1ff) 0%, #ffffff 65%);
        box-shadow: 0 10px 32px rgba(16, 24, 40, 0.1), 0 2px 6px rgba(16, 24, 40, 0.05);
        overflow: hidden;
        font-family: inherit;
        animation: toast-slide-in 0.25s cubic-bezier(0.16, 1, 0.3, 1);
      }
      @keyframes toast-slide-in {
        from {
          opacity: 0;
          transform: translateY(-6px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .toast-icon {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--toast-accent, #3d5af1);
      }
      .toast-icon mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        color: #fff;
      }
      .toast-body {
        flex: 1;
        min-width: 0;
      }
      .toast-title {
        font-size: 15px;
        font-weight: 700;
        color: #1a1f2b;
        margin-bottom: 2px;
        letter-spacing: 0.1px;
      }
      .toast-message {
        font-size: 13.5px;
        line-height: 1.45;
        color: #6b7280;
        word-break: break-word;
      }
      .toast-action {
        margin-top: 8px;
        padding: 4px 12px;
        border: 1px solid var(--toast-border, #c7d2fe);
        border-radius: 6px;
        background: #fff;
        color: var(--toast-accent, #3d5af1);
        font-size: 12.5px;
        font-weight: 600;
        font-family: inherit;
        cursor: pointer;
        transition: background 0.15s ease;
      }
      .toast-action:hover {
        background: var(--toast-tint, #eef1ff);
      }
      .toast-close {
        position: absolute;
        top: 12px;
        right: 12px;
        width: 26px;
        height: 26px;
        border: none;
        background: transparent;
        color: #374151;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background 0.15s ease;
      }
      .toast-close mat-icon {
        font-size: 17px;
        width: 17px;
        height: 17px;
      }
      .toast-close:hover {
        background: rgba(0, 0, 0, 0.06);
      }
      .toast-progress {
        position: absolute;
        left: 0;
        bottom: 0;
        height: 2.5px;
        width: 100%;
        background: var(--toast-accent, #3d5af1);
        opacity: 0.5;
        transform-origin: left;
        transform: scaleX(1);
      }
      .toast-progress.run {
        animation-name: toast-progress-shrink;
        animation-timing-function: linear;
        animation-fill-mode: forwards;
      }
      .toast-progress.run.paused {
        animation-play-state: paused;
      }
      @keyframes toast-progress-shrink {
        from {
          transform: scaleX(1);
        }
        to {
          transform: scaleX(0);
        }
      }
      .toast-success {
        --toast-accent: #22c55e;
        --toast-border: #86efac;
        --toast-tint: #f0fdf4;
      }
      .toast-error {
        --toast-accent: #ef4444;
        --toast-border: #fca5a5;
        --toast-tint: #fef2f2;
      }
      .toast-warning {
        --toast-accent: #f59e0b;
        --toast-border: #fcd34d;
        --toast-tint: #fffbeb;
      }
      .toast-info {
        --toast-accent: #3b82f6;
        --toast-border: #93c5fd;
        --toast-tint: #eff6ff;
      }
    `,
  ],
})
export class ToastSnackbarComponent implements OnInit, OnDestroy {
  isPaused = false;
  hasEntered = false;
  private remaining: number;
  private timerStartedAt = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    public snackBarRef: MatSnackBarRef<ToastSnackbarComponent>,
    @Inject(MAT_SNACK_BAR_DATA) public data: ToastData
  ) {
    this.remaining = data.duration;
  }

  ngOnInit() {
    // Wait until the snack bar's own enter transition has actually finished
    // before starting the countdown — starting it at creation time made the
    // progress bar look like it "jumped" ahead the moment the toast appeared,
    // since it had already been shrinking silently during the enter animation.
    this.snackBarRef.afterOpened().subscribe(() => {
      this.hasEntered = true;
      this.resume();
    });
  }

  ngOnDestroy() {
    clearTimeout(this.timer);
  }

  pause() {
    if (!this.hasEntered || this.isPaused) return;
    this.isPaused = true;
    clearTimeout(this.timer);
    this.remaining -= Date.now() - this.timerStartedAt;
  }

  resume() {
    this.isPaused = false;
    this.timerStartedAt = Date.now();
    this.timer = setTimeout(() => this.snackBarRef.dismiss(), this.remaining);
  }
}
